import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View, type ViewStyle } from 'react-native';
import Animated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { BodyText, Icon, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders an animated, dismissible location message positioned relative to its
 * owning header control.
 */
export const LocationTooltip = (props: Props) => {
  const {
    horizontalAlignment,
    horizontalOffset = 0,
    isVisible,
    messageVariant = 'distance-warning',
    orderChannel,
    topOffset = 0,
    onDismiss,
  } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const message =
    messageVariant === 'location-updated' ? messages.locationUpdated : messages.distanceWarning;

  // ─── Styles ─────────────────────────────────────────────────────────

  const tooltipHorizontalPositionStyle = useStyle(
    () =>
      horizontalAlignment === 'start' ? { left: horizontalOffset } : { right: horizontalOffset },
    [horizontalAlignment, horizontalOffset],
  );

  const tooltipTopOffsetStyle = useStyle(
    () => ({ marginTop: TOOLTIP_ARROW_SIZE + topOffset }),
    [topOffset],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!isVisible) return null;

  return (
    <Animated.View
      accessibilityRole="alert"
      entering={TOOLTIP_ENTERING_ANIMATION}
      exiting={TOOLTIP_EXITING_ANIMATION}
      layout={TOOLTIP_LAYOUT_TRANSITION}
      style={[styles.tooltipContainer, tooltipHorizontalPositionStyle, tooltipTopOffsetStyle]}
    >
      <TouchableOpacity
        accessibilityLabel={formatMessage(messages.dismiss)}
        accessibilityRole="button"
        activeOpacity={0.8}
        onPress={onDismiss}
      >
        <View style={styles.innerShadowContainer}>
          <View style={styles.tooltip}>
            <BodyText sizeMatch={['14']} style={styles.message} maxFontSizeMultiplier={1.2}>
              <FormattedMessage {...message} values={{ order_channel: orderChannel }} />
            </BodyText>

            <View style={styles.iconContainer}>
              <Icon name="IconClose" width={18} height={18} />
            </View>
          </View>
        </View>

        <View style={[styles.arrow, ARROW_HORIZONTAL_STYLES[horizontalAlignment]]} />
      </TouchableOpacity>
    </Animated.View>
  );
};

// ─── Messages ─────────────────────────────────────────────────────────────────

const messages = defineMessages({
  distanceWarning: {
    defaultMessage: 'You seem far away. Is this the right address?',
    description: 'Location tooltip | Distance warning',
  },
  locationUpdated: {
    defaultMessage:
      "We've updated your {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} location based on a previously selected store.",
    description: 'Location tooltip | Previously selected location restored',
  },
  dismiss: {
    defaultMessage: 'Dismiss location warning',
    description: 'Location tooltip | Dismiss button accessibility label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const TOOLTIP_MAX_WIDTH = 312;
const TOOLTIP_ARROW_SIZE = 16;
const TOOLTIP_ARROW_EDGE_OFFSET = theme.spacing['3'];

const TOOLTIP_ENTERING_ANIMATION = FadeIn.duration(theme.transitions.base);
const TOOLTIP_EXITING_ANIMATION = FadeOut.duration(theme.transitions.base);
const TOOLTIP_LAYOUT_TRANSITION = LinearTransition.duration(theme.transitions.base);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  tooltipContainer: {
    position: 'absolute',
    top: '100%',
    maxWidth: TOOLTIP_MAX_WIDTH,
  },
  innerShadowContainer: {
    borderRadius: theme.radius.small,
    boxShadow: '0 0 4px 0 rgba(14, 21, 14, 0.24)',
  },
  tooltip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['3'],
    backgroundColor: theme.colors.NEUTRAL_7,
    borderRadius: theme.radius.small,
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['3'],
  },
  iconContainer: {
    flexShrink: 0,
  },
  message: {
    flexShrink: 1,
  },
  arrow: {
    position: 'absolute',
    top: -(TOOLTIP_ARROW_SIZE / 3),
    width: TOOLTIP_ARROW_SIZE,
    height: TOOLTIP_ARROW_SIZE,
    transform: [{ rotate: '45deg' }],
    backgroundColor: theme.colors.NEUTRAL_7,
    borderRadius: 2,
    zIndex: 1,
  },
  arrowStart: {
    left: TOOLTIP_ARROW_EDGE_OFFSET,
  },
  arrowEnd: {
    right: TOOLTIP_ARROW_EDGE_OFFSET,
  },
});

const ARROW_HORIZONTAL_STYLES: Record<HorizontalAlignment, ViewStyle> = {
  start: styles.arrowStart,
  end: styles.arrowEnd,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  messageVariant?: MessageVariant;
  orderChannel: OrderChannel;
  horizontalAlignment: HorizontalAlignment;
  topOffset?: number;
  horizontalOffset?: number;
  onDismiss: () => void;
};

type HorizontalAlignment = 'end' | 'start';
type MessageVariant = 'distance-warning' | 'location-updated';
type OrderChannel = 'delivery' | 'outpost' | 'pickup';
